import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';

import BudgetStep from '@/components/organisms/budget/form-steps/BudgetStep';
import MembersStep from '@/components/organisms/budget/form-steps/MembersStep';
import {
  budgetFormSchema,
  budgetStep1FieldNames,
  getMembersStepFieldNames,
  type BudgetFormValues,
  type BudgetMemberFormValues
} from '@/components/organisms/budget/form-steps/budgetStep.schema';
import { getCurrentUser } from '@/store/slices/auth';

import { BUDGET_ICONS } from '@/utils/budgetIcons';
import { getBudgetMemberCalculations, type BudgetMemberCalculations } from '@/utils/budgetCalculations';
import { budgetMemberService } from '@/services/budget-member.service';

export interface BudgetCreationFormHandle {
  validateStep: (step: number) => Promise<boolean>;
  submit: () => void;
  getMembers: () => BudgetMemberFormValues[];
}

interface BudgetCreationFormProps {
  activeStep: number;
  onSubmit?: (data: BudgetFormValues) => void;
  className?: string;
  initialValues?: Partial<BudgetFormValues>;
  onFormStateChange?: (state: { isDirty: boolean }) => void;
  onMemberCalculationsChange?: (calculations: BudgetMemberCalculations) => void;
}

const createOwnerMember = (
  userId: string,
  email: string,
  memberName = '',
  hourlyRate = 0,
  profilePicUrl: string | null = null
): BudgetMemberFormValues => ({
  memberId: '',
  userId,
  email,
  memberName,
  profilePicUrl,
  type: '',
  oreHours: '' as unknown as number,
  hourlyRate,
  role: 'owner'
});

const BudgetCreationForm = forwardRef<BudgetCreationFormHandle, BudgetCreationFormProps>(
  ({ activeStep, onSubmit, className, initialValues, onFormStateChange, onMemberCalculationsChange }, ref) => {
    const currentUser = useSelector(getCurrentUser);
    const [isInvitingMember, setIsInvitingMember] = useState(false);

    const defaultOwnerMember = useMemo(
      () =>
        createOwnerMember(
          currentUser?.id ?? '',
          currentUser?.email ?? '',
          currentUser?.name ?? '',
          currentUser?.hourlyRate ?? 0,
          currentUser?.photoURL ?? null
        ),
      [currentUser?.id, currentUser?.email, currentUser?.name, currentUser?.hourlyRate, currentUser?.photoURL]
    );

    const defaultValues: BudgetFormValues = {
      budgetName: '',
      budgetType: '',
      budgetIcon: '',
      icon_id: Object.keys(BUDGET_ICONS)[0] || '',
      budgetColor: '',
      description: '',
      startDate: new Date(),
      endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)),
      currency: 'EUR',
      period: 'one_month',
      autoRenewal: false,
      bmev: '' as unknown as number,
      bmevThreshold: 50,
      btrt: '' as unknown as number,
      btrtThreshold: 50,
      members: [defaultOwnerMember],
      inviteEmail: ''
    };

    const {
      register,
      handleSubmit,
      watch,
      setValue,
      reset,
      trigger,
      getValues,
      control,
      formState: { errors, isDirty },
      setError,
      clearErrors
    } = useForm<BudgetFormValues>({
      resolver: zodResolver(budgetFormSchema),
      defaultValues,
      mode: 'onChange'
    });

    const { fields, append, remove } = useFieldArray({
      control,
      name: 'members'
    });

    useEffect(() => {
      if (initialValues) {
        reset({
          ...defaultValues,
          ...initialValues,
          members: initialValues.members?.length ? initialValues.members : [defaultOwnerMember]
        });
        return;
      }

      const members = getValues('members');
      if (!members?.length) {
        setValue('members', [defaultOwnerMember]);
      } else if (currentUser?.id && members[0]?.role === 'owner') {
        if (!members[0]?.userId) {
          setValue('members.0.userId', currentUser.id);
          setValue('members.0.email', currentUser.email);
        }
        setValue('members.0.memberName', currentUser.name ?? members[0]?.memberName ?? '');
        setValue('members.0.profilePicUrl', currentUser.photoURL ?? null);
        setValue('members.0.hourlyRate', currentUser.hourlyRate ?? 0);
      }
    }, [initialValues, reset, defaultOwnerMember, currentUser, getValues, setValue]);

    useEffect(() => {
      onFormStateChange?.({ isDirty });
    }, [isDirty, onFormStateChange]);

    useEffect(() => {
      if (!onMemberCalculationsChange) return;

      let timeoutId: ReturnType<typeof setTimeout>;

      const emitCalculations = (values: BudgetFormValues) => {
        onMemberCalculationsChange(
          getBudgetMemberCalculations(values.members ?? [], values.bmev, values.currency ?? 'EUR')
        );
      };

      emitCalculations(getValues());

      const subscription = watch((value) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          emitCalculations(value as BudgetFormValues);
        }, 300);
      });

      return () => {
        subscription.unsubscribe();
        clearTimeout(timeoutId);
      };
    }, [watch, getValues, onMemberCalculationsChange]);

    const calculatePeriod = (startDate: Date, endDate: Date): string => {
      const diffMonths =
        (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());

      if (diffMonths <= 1) {
        return 'one_month';
      } else if (diffMonths <= 6) {
        return 'six_month';
      } else {
        return 'one_year';
      }
    };

    useEffect(() => {
      const subscription = watch((value, { name }) => {
        const startDate = value.startDate as Date;
        const endDate = value.endDate as Date;
        const currentPeriod = value.period as string;

        if (name === 'startDate' && startDate && currentPeriod) {
          let newEndDate = new Date(startDate);

          if (currentPeriod === 'one_month') {
            newEndDate.setMonth(startDate.getMonth() + 1);
          } else if (currentPeriod === 'six_month') {
            newEndDate.setMonth(startDate.getMonth() + 6);
          } else if (currentPeriod === 'one_year') {
            newEndDate.setFullYear(startDate.getFullYear() + 1);
          }

          setValue('endDate', newEndDate);
        } else if (name === 'endDate' && startDate && endDate) {
          const newPeriod = calculatePeriod(startDate, endDate);
          if (newPeriod !== currentPeriod) {
            setValue('period', newPeriod);
          }
        } else if (name === 'period' && startDate && currentPeriod) {
          let newEndDate = new Date(startDate);

          if (currentPeriod === 'one_month') {
            newEndDate.setMonth(startDate.getMonth() + 1);
          } else if (currentPeriod === 'six_month') {
            newEndDate.setMonth(startDate.getMonth() + 6);
          } else if (currentPeriod === 'one_year') {
            newEndDate.setFullYear(startDate.getFullYear() + 1);
          }

          setValue('endDate', newEndDate);
        }
      });

      return () => subscription.unsubscribe();
    }, [watch, setValue]);

    const iconSelectRef = useRef<HTMLDivElement>(null);

    const onSubmitForm = (data: BudgetFormValues) => {
      if (onSubmit) {
        onSubmit(data);
      }
    };

    const onInvalidForm = (formErrors: typeof errors) => {
      toast.error('Please fill all required fields');
      if (formErrors.budgetIcon) {
        iconSelectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        iconSelectRef.current?.focus();
      }
    };

    const handleInviteMember = async () => {
      const email = getValues('inviteEmail').trim();
      const members = getValues('members') ?? [];
      const pendingEmails = members.map((member) => member.email).filter(Boolean);

      try {
        setIsInvitingMember(true);
        clearErrors('inviteEmail');

        const result = await budgetMemberService.validateMemberEmail({
          email,
          pending_emails: pendingEmails
        });

        append({
          memberId: '',
          userId: result.user_id,
          email: result.email,
          memberName: result.name,
          profilePicUrl: result.profile_pic_url,
          type: '',
          oreHours: '' as unknown as number,
          hourlyRate: result.hourly_rate ?? 0,
          role: 'staff'
        });

        setValue('inviteEmail', '', { shouldDirty: true });
        clearErrors('inviteEmail');
      } catch (error: any) {
        setError('inviteEmail', {
          type: 'manual',
          message: error?.message ?? 'Failed to validate email. Please try again.'
        });
      } finally {
        setIsInvitingMember(false);
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        validateStep: async (step: number) => {
          if (step === 1) {
            const isValid = await trigger([...budgetStep1FieldNames]);
            if (!isValid) {
              toast.error('Please fill all required fields');
              if (!watch('budgetIcon')) {
                iconSelectRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                iconSelectRef.current?.focus();
              }
            }
            return isValid;
          }
          if (step === 2) {
            const members = getValues('members') ?? [];
            const isValid = await trigger(getMembersStepFieldNames(members.length) as any);
            if (!isValid) {
              toast.error('Please fill all required member fields');
            }
            return isValid;
          }
          return false;
        },
        submit: () => {
          handleSubmit(onSubmitForm, onInvalidForm)();
        },
        getMembers: () => getValues('members') ?? []
      }),
      [trigger, watch, handleSubmit, onSubmit, getValues]
    );

    return (
      <form
        id="budget-creation-form"
        className={`space-y-6 ${className}`}
        onSubmit={handleSubmit(onSubmitForm, onInvalidForm)}
      >
        {activeStep === 1 && (
          <BudgetStep
            register={register}
            watch={watch}
            setValue={setValue}
            trigger={trigger}
            errors={errors}
            iconSelectRef={iconSelectRef}
          />
        )}

        {activeStep === 2 && (
          <MembersStep
            register={register}
            watch={watch}
            setValue={setValue}
            errors={errors}
            fields={fields}
            remove={remove}
            onInviteMember={handleInviteMember}
            isInvitingMember={isInvitingMember}
            setError={setError}
            clearErrors={clearErrors}
            currentUserEmail={currentUser?.email}
            currentUserPhotoUrl={currentUser?.photoURL}
            currency={watch('currency')}
          />
        )}
      </form>
    );
  }
);

BudgetCreationForm.displayName = 'BudgetCreationForm';

export default BudgetCreationForm;
